import { MsalAuthenticationTemplate } from '@azure/msal-react';
import { InteractionType } from '@azure/msal-browser';
import { loginRequest, protectedResources } from "../authConfig";
import LoadingSpinner from "../components/LoadingSpinner";
import React, { useEffect, useState, useCallback, useRef } from 'react';
import useFetchWithMsal from '../hooks/useFetchWithMsal';
import { styled } from '@mui/material/styles';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Button from 'react-bootstrap/Button';
import DataTable from 'react-data-table-component';

import { DateTime } from "luxon";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${ tableCellClasses.head }`]: {
        backgroundColor: theme.palette.common.black,
        color: theme.palette.common.white,
    },
    [`&.${ tableCellClasses.body }`]: {
        fontSize: 14,
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    // hide last border
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));

export const HistoricalRecordGrid = (props) => {
    const STORAGE_KEY = "HistoricalRecordGrid_filters";

    const authRequest = {
        ...loginRequest,
    };

    const { isLoading, error, execute } = useFetchWithMsal({
        scopes: protectedResources.api.scopes
    });

    const loadStoredFilters = () => {
        const storedFilters = localStorage.getItem(STORAGE_KEY);
        return storedFilters ? JSON.parse(storedFilters) : { fecha: "", nombre: "", email: "", jefe: "", estatus: "" };
    };

    const [data, setData] = useState([]);
    const [filters, setFilters] = useState(loadStoredFilters);
    const [localFilters, setLocalFilters] = useState(loadStoredFilters);
    const [loading, setLoading] = useState(false);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(25);
    const [currentPage, setCurrentPage] = useState(1);
    const [debounceTimeout, setDebounceTimeout] = useState(null);
    const [sortColumn, setSortColumn] = useState('dateAttendance');
    const [sortDirection, setSortDirection] = useState('desc');
    const [isHandlingSortChange, setIsHandlingSortChange] = useState(false);
    const [dateRange, setDateRange] = useState([]);
    
    const clearGridFilters = useCallback(() => {
        const emptyFilters = { fecha: "", nombre: "", email: "", jefe: "", estatus: "" };
        setLocalFilters(emptyFilters);
        setFilters(emptyFilters);
        setDateRange([]);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(emptyFilters));
        
        if (debounceTimeout) {
            clearTimeout(debounceTimeout);
            setDebounceTimeout(null);
        }
    }, [debounceTimeout]);
    
    const clearFunctionRef = useRef();
    clearFunctionRef.current = clearGridFilters;
    
    useEffect(() => {
        if (props.onClearFilters) {
            props.onClearFilters(() => clearFunctionRef.current());
        }
    }, [props.onClearFilters]);

    const fetchHistoricalData = async (page, pageSize) => {
        if (loading) {
            return;
        }
        
        setLoading(true);
        
        let endpoint = protectedResources.api.historicalRecord;
        const params = new URLSearchParams();
        
        if (props.showAll) {
            params.append('showAll', 'true');
        }
        
        // Agregar filtros de fecha si existen
        if (props.dateFilter?.startDate) {
            params.append('startDate', props.dateFilter.startDate);
        }
        if (props.dateFilter?.endDate) {
            params.append('endDate', props.dateFilter.endDate);
        }
        
        if (filters.fecha) {
            params.append('filterFecha', filters.fecha);
        }
        if (filters.nombre) {
            params.append('filterNombre', filters.nombre);
        }
        if (filters.email) {
            params.append('filterEmail', filters.email);
        }
        if (filters.jefe && props.isAdmin) {
            params.append('filterJefe', filters.jefe);
        }
        if (filters.estatus) {
            params.append('filterEstatus', filters.estatus);
        }
        
        params.append('page', page.toString());
        params.append('pageSize', pageSize.toString());
        params.append('sortColumn', sortColumn);
        params.append('sortDirection', sortDirection);
        
        endpoint += `?${params.toString()}`;

        try {
            const response = await execute("GET", endpoint);
            if (response?.data) {
                setData(response.data);
                setTotalRows(response.pagination?.total || 0);
            }
        } catch (error) {
            console.error("Error fetching historical data:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!props.isAdmin && filters.jefe !== "") {
            handleFilterChange("jefe", "");
            return;
        }
        
        if (execute) {
            fetchHistoricalData(currentPage, perPage);
        }
    }, [execute, currentPage, perPage, props.showAll, props.dateFilter, sortColumn, sortDirection]);
    
    useEffect(() => {
        if (execute) {
            setCurrentPage(1);
            fetchHistoricalData(1, perPage);
        }
    }, [filters.fecha, filters.nombre, filters.email, filters.jefe, filters.estatus]);

    useEffect(() => {
        return () => {
            if (debounceTimeout) {
                clearTimeout(debounceTimeout);
            }
        };
    }, [debounceTimeout]);

    const filteredData = data;

    const handleFilterChange = (field, value) => {
        setLocalFilters(prev => {
            const newFilters = { ...prev, [field]: value };
            localStorage.setItem(STORAGE_KEY, JSON.stringify(newFilters));
            return newFilters;
        });
        
        if (debounceTimeout) {
            clearTimeout(debounceTimeout);
        }
        
        const newTimeout = setTimeout(() => {
            setFilters(prev => ({ ...prev, [field]: value }));
        }, 500);
        
        setDebounceTimeout(newTimeout);
    };

    const handlePageChange = (page) => {
        if (isHandlingSortChange || page === currentPage) {
            return;
        }
        setCurrentPage(page);
    };

    const handlePerRowsChange = (newPerPage, page) => {
        if (isHandlingSortChange) {
            return;
        }
        setPerPage(newPerPage);
        setCurrentPage(page);
    };

    const handleSort = (column, sortDirection) => {
        
        if (isHandlingSortChange) {
            return;
        }
        
        if (!column.selector) {
            return;
        }
        
        setIsHandlingSortChange(true);
        
        let backendColumnName;
        
        if (column.id) {
            switch (column.id) {
                case 'dateAttendance':
                    backendColumnName = 'dateAttendance';
                    break;
                case 'status':
                    backendColumnName = 'excuse.status';
                    break;
                default:
                    backendColumnName = column.selector || 'dateAttendance';
            }
        } else if (typeof column.selector === 'function') {
            const selectorString = column.selector.toString();
            
            if (selectorString.includes('employee.name')) {
                backendColumnName = 'employee.name';
            } else if (selectorString.includes('employee.email')) {
                backendColumnName = 'employee.email';
            } else if (selectorString.includes('boss') && selectorString.includes('name')) {
                backendColumnName = 'boss.name';
            } else if (selectorString.includes('handleTextStatusExcuse')) {
                backendColumnName = 'excuse.status';
            } else {
                backendColumnName = 'dateAttendance';
            }
        } else {
            backendColumnName = column.selector || 'dateAttendance';
        }
        
        setSortColumn(backendColumnName);
        setSortDirection(sortDirection);
        if (currentPage !== 1) {
            setCurrentPage(1);
        }
        
        setTimeout(() => setIsHandlingSortChange(false), 100);
    };

    const handlePlaceholderAction = (row) => {
        // Placeholder para futuras funcionalidades
        console.log("Acción futura para:", row);
    };

    const handleTextStatusExcuse = (excuse, attendance) => {
        var status = '';
        if (attendance && attendance.check_in !== null) {
            status = 'Asistencia';
        } else if (excuse) {
            switch (excuse.status) {
                case 0:
                    status = 'Pendiente'
                    break;
                case 1:
                    status = 'Justificada'
                    break;
                case 2:
                    status = 'Rechazada'
                    break;
                default:
                    break;
            }
        } else {
            status = 'Falta'
        }
        return status;
    };

    const columns = [
        {
            id: 'dateAttendance',
            selector: row => {
                const status = handleTextStatusExcuse(row.excuse, row);
                if (status === 'Falta') {
                    return DateTime.fromISO(row.dateAttendance).setZone('America/Mexico_City').plus({ days: 1 }).toFormat('yyyy-MM-dd');
                } else {
                    return DateTime.fromISO(row.createdAt).setZone('America/Mexico_City').toFormat('yyyy-MM-dd HH:mm');
                }
            },
            sortable: true,
            grow: 1,
            cell: row => {
                const status = handleTextStatusExcuse(row.excuse, row);
                if (status === 'Falta') {
                    return DateTime.fromISO(row.dateAttendance).setZone('America/Mexico_City').plus({ days: 1 }).toFormat('yyyy-MM-dd');
                } else {
                    return DateTime.fromISO(row.createdAt).setZone('America/Mexico_City').toFormat('yyyy-MM-dd HH:mm');
                }
            },
            name: "Fecha",
        },
        {
            selector: row => row.employee.name,
            sortable: true,
            grow: 1,
            cell: row => row.employee.name,
            filterComponent: (
                <input
                    type="text"
                    placeholder="Nombre"
                    className="form-control"
                    value={localFilters.nombre}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => handleFilterChange("nombre", e.target.value)}
                />
            )
        },
        {
            selector: row => row.employee.email,
            sortable: true,
            grow: 1,
            cell: row => row.employee.email,
            filterComponent: (
                <input
                    type="text"
                    placeholder="Email"
                    className="form-control"
                    value={localFilters.email}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => handleFilterChange("email", e.target.value)}
                />
            )
        },
        ...(props.isAdmin && props.showAll ? [{
            selector: row => row?.employee?.boss?.name ? row.employee.boss.name.toLowerCase() : 'Sin jefe asignado',
            sortable: true,
            grow: 1,
            cell: row => row?.employee?.boss?.name ? row.employee.boss.name.toLowerCase() : 'Sin jefe asignado',
            filterComponent: (
                <input
                    type="text"
                    placeholder="Jefe"
                    className="form-control"
                    value={localFilters.jefe}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => handleFilterChange("jefe", e.target.value)}
                />
            )
        }] : []),
        {
            id: 'status',
            selector: row => handleTextStatusExcuse(row.excuse, row),
            // sortable: true,
            grow: 1,
            cell: row => {
                const status = handleTextStatusExcuse(row.excuse, row);
                let className = '';
                switch (status) {
                    case 'Justificada':
                        className = 'text-success';
                        break;
                    case 'Rechazada':
                        className = 'text-danger';
                        break;
                    case 'Pendiente':
                        className = 'text-warning';
                        break;
                    case 'Falta':
                        className = 'text-muted';
                        break;
                    case 'Asistencia':
                        className = 'text-primary';
                        break;
                    default:
                        className = '';
                }
                return <span className={className}>{status}</span>;
            },
            filterComponent: (
                <select
                    className="form-control"
                    value={localFilters.estatus}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => handleFilterChange("estatus", e.target.value)}
                >
                    <option value="">Todos</option>
                    <option value="0">Pendiente</option>
                    <option value="1">Justificada</option>
                    <option value="2">Rechazada</option>
                    <option value="falta">Falta</option>
                    <option value="asistencia">Asistencia</option>
                </select>
            )
        },
        // {
        //     name: "Acción",
        //     cell: row => (
        //         <Button 
        //             variant="secondary" 
        //             size="sm"
        //             onClick={() => handlePlaceholderAction(row)}
        //             disabled
        //         >
        //             Próximamente
        //         </Button>
        //     ),
        //     ignoreRowClick: true,
        //     allowOverflow: true,
        //     button: true,
        //     minWidth: '150px'
        // },
    ];

    const renderForm = (
        <MsalAuthenticationTemplate interactionType={InteractionType.Redirect} authenticationRequest={authRequest}>
            <div className="container">
                {(data.length > 0 || totalRows > 0) && (
                    <div className="mb-3">
                        <small className="text-muted">
                            {localFilters.fecha || localFilters.nombre || localFilters.email || localFilters.jefe || localFilters.estatus ? (
                                `Mostrando ${data.length} registros filtrados desde el servidor (Página ${currentPage} de ${Math.ceil(totalRows / perPage)} - Total filtrado: ${totalRows})`
                            ) : (
                                `Mostrando ${data.length} de ${totalRows} registros (Página ${currentPage} de ${Math.ceil(totalRows / perPage)})`
                            )}
                            {props.dateFilter?.startDate && props.dateFilter?.endDate && (
                                ` | Filtro: ${props.dateFilter.startDate} a ${props.dateFilter.endDate}`
                            )}
                            {props.dateFilter?.startDate && !props.dateFilter?.endDate && (
                                ` | Filtro: desde ${props.dateFilter.startDate}`
                            )}
                            {!props.dateFilter?.startDate && props.dateFilter?.endDate && (
                                ` | Filtro: hasta ${props.dateFilter.endDate}`
                            )}
                        </small>
                    </div>
                )}
                <DataTable
                    columns={columns.map(col => ({
                        ...col,
                        name: (
                            <div style={{ textAlign: 'center', width: '100%' }}>
                                {col.name}
                                {col.filterComponent && <div>{col.filterComponent}</div>}
                            </div>
                        )
                    }))}
                    data={filteredData}
                    progressPending={loading}
                    pagination
                    paginationServer
                    paginationTotalRows={totalRows}
                    paginationDefaultPage={currentPage}
                    paginationPerPage={perPage}
                    paginationRowsPerPageOptions={[25, 50, 100, 200]}
                    onChangeRowsPerPage={handlePerRowsChange}
                    onChangePage={handlePageChange}
                    sortServer
                    onSort={handleSort}
                    highlightOnHover
                    responsive
                    fixedHeader
                    noDataComponent="No hay datos disponibles"
                    persistTableHead
                />
            </div>
        </MsalAuthenticationTemplate>
    );

    return (
        <div className="container"> {isLoading ? <LoadingSpinner /> : renderForm}</div>
    );
}